import React from 'react';
import { toast, Slide } from 'react-toastify';

/**
 * Copies text to clipboard
 * @param {string} text - Text to copy to clipboard
 * @param {Object} options - Configuration options
 * @param {string} options.successMessage - Success message to show
 * @param {string} options.position - Toast position
 * @param {number} options.autoClose - Auto close duration in ms
 * @returns {boolean} - Whether copy was successful
 */
export const copyToClipboard = (text, options = {}) => {
  const {
    successMessage = 'Copied to clipboard',
    position = 'bottom-right',
    autoClose = 2000
  } = options;

  try {
    // Use the modern Clipboard API if available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      toast.success(successMessage, {
        position,
        autoClose,
        transition: Slide
      });
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed'; // Avoid scrolling to bottom
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(successMessage, {
          position,
          autoClose,
          transition: Slide
        });
        return true;
      } else {
        toast.error('Unable to copy to clipboard', {
          position,
          autoClose,
          transition: Slide
        });
        return false;
      }
    }
  } catch (err) {
    toast.error(`Failed to copy: ${err.message}`, {
      position,
      autoClose,
      transition: Slide
    });
    return false;
  }
};

/**
 * React component that provides copy to clipboard functionality
 * @param {Object} props - Component props
 * @param {string} props.text - Text to copy to clipboard
 * @param {ReactNode} props.children - Child elements
 * @param {string} props.successMessage - Message to show on successful copy
 * @param {string} props.position - Toast position
 * @param {number} props.autoClose - Auto close duration in ms
 * @param {Function} props.onCopy - Callback function called after copying
 * @param {Object} props.style - Additional styles for the wrapper
 * @param {string} props.className - Additional CSS classes
 */
export const CopyToClipboard = ({
  text,
  children,
  successMessage = 'Copied to clipboard',
  position = 'bottom-right',
  autoClose = 2000,
  onCopy,
  className = 'hover-pointer',
  style = {},
  ...props
}) => {
  const handleClick = () => {
    const result = copyToClipboard(text, { successMessage, position, autoClose });
    if (onCopy && typeof onCopy === 'function') {
      onCopy(text, result);
    }
  };

  return (
    <div 
      onClick={handleClick} 
      className={className}
      style={{ cursor: 'pointer', ...style }}
      {...props}
    >
      {children}
    </div>
  );
};